import { useCallback, useEffect, useMemo, useState } from 'react';
import { todayIsoParis } from '@lemontaigu/staff';
import type { AvailabilityCell, Room, RoomType } from '@lemontaigu/pms-core';
import { usePms } from './PmsProvider.js';
import { fmtDayShort, isoAddDays, isoRange, sortByRoomCategory } from './logic.js';

// « Calendrier » (PMS P1, bos.*) — the derived availability grid (room type ×
// day, from pms_availability_range) + the room-typing panel. The grid is the
// P1 exit-criterion tool: it must match the live SiteMinder/Amenitiz position
// for a hand-mirrored week. Room→type mapping is operator knowledge (Hard
// Rule 1): the 42 rooms are seeded UNTYPED and assigned here.

const DAYS_SHOWN = 14;

export function Calendrier() {
  const pms = usePms();
  const todayIso = useMemo(() => todayIsoParis(), []);
  const [startIso, setStartIso] = useState(todayIso);
  const [avail, setAvail] = useState<AvailabilityCell[] | null>(null);
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [showRooms, setShowRooms] = useState(false);
  const [err, setErr] = useState('');

  const days = useMemo(() => isoRange(startIso, DAYS_SHOWN), [startIso]);

  const load = useCallback(async () => {
    setErr('');
    const [av, types, rms] = await Promise.all([
      pms.availability.calendar(startIso, isoAddDays(startIso, DAYS_SHOWN - 1)),
      pms.reference.roomTypes(),
      pms.reference.rooms(),
    ]);
    const bad = [av, types, rms].find((r) => !r.ok);
    if (bad && !bad.ok) {
      setErr(bad.error.message);
      return;
    }
    if (av.ok) setAvail(av.value);
    // Sorted once, here: the canonical category order (DSUP, DSTD, TSUP, TSTD,
    // FAMS, FAM) is the operator's, and both the grid rows and the assignment
    // select read from this one list — they can never disagree.
    if (types.ok) setRoomTypes(sortByRoomCategory(types.value));
    if (rms.ok) setRooms(rms.value);
  }, [pms, startIso]);

  useEffect(() => {
    void load();
  }, [load]);

  // cell lookup: `${code}|${day}` → availability row
  const cells = useMemo(() => {
    const m = new Map<string, AvailabilityCell>();
    for (const a of avail ?? []) m.set(`${a.code}|${a.day}`, a);
    return m;
  }, [avail]);

  const untyped = rooms.filter((r) => r.roomTypeId === null);
  const floors = useMemo(() => {
    const m = new Map<number, Room[]>();
    for (const r of rooms) {
      const list = m.get(r.floor) ?? [];
      list.push(r);
      m.set(r.floor, list);
    }
    return [...m.entries()].sort(([a], [b]) => a - b);
  }, [rooms]);

  const typeById = useMemo(() => new Map(roomTypes.map((t) => [t.id, t])), [roomTypes]);

  const cellClass = (available: number, total: number) => {
    if (total === 0) return 'pms-cal-cell is-untyped';
    if (available < 0) return 'pms-cal-cell is-over';
    if (available === 0) return 'pms-cal-cell is-full';
    return 'pms-cal-cell';
  };

  return (
    <div className="pms">
      <div className="pms-head">
        <h2 className="section-title">Calendrier — disponibilités PMS</h2>
        <span className="pms-hint">
          Dérivé (chambres typées − réservations actives − blocages) — à comparer au tableau SiteMinder (critère de
          sortie P1)
        </span>
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}

      {untyped.length > 0 && (
        <p className="pms-warn">
          ⚠ {untyped.length} chambre{untyped.length > 1 ? 's' : ''} sans catégorie — le calendrier ne compte que les
          chambres typées.{' '}
          <button type="button" className="pms-link" onClick={() => setShowRooms(true)}>
            Assigner les catégories
          </button>
        </p>
      )}

      <div className="pms-toolbar">
        <div className="pms-chips">
          <button type="button" className="pms-chip" onClick={() => setStartIso(isoAddDays(startIso, -7))}>
            ← Semaine
          </button>
          <button type="button" className="pms-chip" onClick={() => setStartIso(todayIso)}>
            Aujourd’hui
          </button>
          <button type="button" className="pms-chip" onClick={() => setStartIso(isoAddDays(startIso, 7))}>
            Semaine →
          </button>
        </div>
        <button type="button" className="auth-btn is-ghost" onClick={() => setShowRooms((s) => !s)}>
          {showRooms ? 'Masquer les chambres' : `Chambres (${rooms.length})`}
        </button>
      </div>

      <div className="pms-table-wrap">
        <table className="pms-cal">
          <thead>
            <tr>
              <th className="pms-cal-type">Catégorie</th>
              {days.map((d) => (
                <th key={d} className={d === todayIso ? 'is-today' : ''}>
                  {fmtDayShort(d)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {avail === null && (
              <tr>
                <td colSpan={DAYS_SHOWN + 1} className="pms-empty">
                  Chargement…
                </td>
              </tr>
            )}
            {roomTypes.map((t) => (
              // The WHOLE row carries the capacity colour (operator,
              // 2026-08-07). The availability cells still paint over it when
              // they have something to say — complet, surbooké, non typé — so
              // the heat scale survives as the exception on a coloured row.
              <tr key={t.id} className={`pms-occ is-${t.maxOccupancy}`}>
                <td className="pms-cal-type">
                  <strong>{t.code}</strong>
                  <span className="pms-sub">{t.labelFr}</span>
                </td>
                {days.map((d) => {
                  const c = cells.get(`${t.code}|${d}`);
                  if (!c) return <td key={d} className="pms-cal-cell">·</td>;
                  return (
                    <td key={d} className={cellClass(c.available, c.total)} title={`${c.occupied} occ. · ${c.blocked} bloq. · ${c.total} ch.`}>
                      {c.total === 0 ? '·' : c.available}
                    </td>
                  );
                })}
              </tr>
            ))}
            {avail !== null && (
              <tr className="pms-cal-total">
                <td className="pms-cal-type">
                  <strong>TOTAL</strong>
                </td>
                {days.map((d) => {
                  const dayRows = (avail ?? []).filter((a) => a.day === d);
                  const sum = dayRows.reduce((s, a) => s + a.available, 0);
                  const total = dayRows.reduce((s, a) => s + a.total, 0);
                  return (
                    <td key={d} className={cellClass(sum, total)}>
                      {total === 0 ? '·' : sum}
                    </td>
                  );
                })}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showRooms && (
        <section className="pms-rooms">
          <h3>Chambres — catégories (42)</h3>
          <p className="pms-hint">
            Lecture seule : la catégorie de chaque chambre s’assigne dans la fiche chambre, page Configuration.
          </p>
          {floors.map(([floor, floorRooms]) => (
            <div key={floor} className="pms-floor">
              <h4>Étage {floor}</h4>
              <div className="pms-floor-grid">
                {floorRooms.map((room) => (
                  <div
                    key={room.id}
                    className={[
                      'pms-room-card',
                      // Capacity tints the whole card — same palette as the
                      // Configuration band and the Calendrier rows.
                      room.maxOccupancy !== null ? `pms-occ is-${room.maxOccupancy}` : '',
                      // Untyped is a to-do, and its amber must still win: an
                      // uncategorised room is what this panel exists to surface.
                      room.roomTypeId === null ? 'is-untyped' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <div className="pms-room-card-top">
                      <span className="pms-room-no">
                        {room.roomNumber}
                        {room.isSuperior && <span className="pms-room-sup">SUP</span>}
                      </span>
                      {/* READ-ONLY (operator, 2026-08-07). Assignment moved to
                          the fiche chambre in Configuration, which is now the
                          one write path for room configuration — this panel
                          shows the state, it no longer changes it. */}
                      <span className="pms-room-cat" title={typeById.get(room.roomTypeId ?? -1)?.labelFr}>
                        {typeById.get(room.roomTypeId ?? -1)?.code ?? '—'}
                      </span>
                    </div>
                    <div className="pms-room-meta">
                      <span className="pms-room-bed">{room.configOptions.join(' / ') || '—'}</span>
                      {/* Plain text again: the card's own background now
                          carries the capacity colour, so a badge would say the
                          same thing twice. */}
                      <span>· {room.maxOccupancy ?? '?'} pers.</span>
                      {room.view && <span>· {room.view}</span>}
                    </div>
                    <div className="pms-room-badges">
                      <span className="pms-room-zone" title="Zone de chauffage (chambre / salle de bain)">
                        🔥 {room.heatingZone ?? '?'}
                        {room.bathroomHeatingZone != null ? ` · SdB ${room.bathroomHeatingZone}` : ''}
                      </span>
                      {room.hasShower && <span className="pms-room-tag" title="Douche (sinon baignoire)">Douche</span>}
                      {room.wcNotSeparated && <span className="pms-room-tag" title="WC non séparé">WC NS</span>}
                      {room.remarks && <span className="pms-room-tag is-rem" title={room.remarks}>{room.remarks}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
